/**
 * AdsManager Metrics Calculation Utilities
 * Centralized functions for calculating ad performance metrics
 */

import { AdsDailyBreakdown } from '../types/ads.types';

/**
 * Calculate Return on Ad Spend (ROAS)
 * @param revenue Total revenue generated
 * @param spend Total ad spend
 * @returns ROAS as a decimal (e.g., 1.5 means 150%)
 */
export const calculateROAS = (revenue: number, spend: number): number => {
  return spend > 0 ? revenue / spend : 0;
};

/**
 * Calculate Revenue Per Mille (RPM) - Revenue per 1000 searches
 * @param revenue Total revenue
 * @param searches Total searches
 * @returns RPM value
 */
export const calculateRPM = (revenue: number, searches: number): number => {
  return searches > 0 ? (revenue / searches) * 1000 : 0;
};

/**
 * Calculate Revenue Per Click (RPC)
 * @param revenue Total revenue
 * @param clicks Total clicks
 * @returns RPC value
 */
export const calculateRPC = (revenue: number, clicks: number): number => {
  return clicks > 0 ? revenue / clicks : 0;
};

/**
 * Calculate Cost Per Action (CPA) - Cost per search
 * @param spend Total ad spend
 * @param searches Total searches
 * @returns CPA value
 */
export const calculateCPA = (spend: number, searches: number): number => {
  return searches > 0 ? spend / searches : 0;
};

/**
 * Calculate Cost Per Click (CPC)
 * @param spend Total ad spend
 * @param clicks Total clicks
 * @returns CPC value
 */
export const calculateCPC = (spend: number, clicks: number): number => {
  return clicks > 0 ? spend / clicks : 0;
};

/**
 * Calculate Page Click-Through Rate (CTR)
 * @param clicks Total clicks
 * @param searches Total searches
 * @returns CTR as a percentage (0-100)
 */
export const calculatePageCTR = (clicks: number, searches: number): number => {
  return searches > 0 ? (clicks / searches) * 100 : 0;
};

/**
 * Calculate Coverage from daily breakdown data
 * Coverage represents the percentage of searches that were monetized
 * @param dailyBreakdown Array of daily breakdown data
 * @returns Coverage as a percentage (0-100)
 */
export const calculateCoverage = (dailyBreakdown?: AdsDailyBreakdown[]): number => {
  if (!dailyBreakdown || dailyBreakdown.length === 0) {
    return 0;
  }

  let totalMonetizedSearches = 0;
  let totalSearches = 0;

  dailyBreakdown.forEach((day) => {
    const searches = day.searches || 0;
    const coverage = day.coverage || 0;
    // Calculate monetized searches from the coverage percentage
    const monetizedSearches = (coverage / 100) * searches;
    totalMonetizedSearches += monetizedSearches;
    totalSearches += searches;
  });

  return totalSearches > 0 ? (totalMonetizedSearches / totalSearches) * 100 : 0;
};

/**
 * Calculate Page CTR from daily breakdown data
 * @param dailyBreakdown Array of daily breakdown data
 * @returns Page CTR as a percentage (0-100)
 */
export const calculatePageCTRFromBreakdown = (
  dailyBreakdown?: AdsDailyBreakdown[]
): number => {
  if (!dailyBreakdown || dailyBreakdown.length === 0) {
    return 0;
  }

  let totalClicks = 0;
  let totalSearches = 0;

  dailyBreakdown.forEach((day) => {
    totalClicks += day.clicks || 0;
    totalSearches += day.searches || 0;
  });

  return calculatePageCTR(totalClicks, totalSearches);
};

/**
 * Get earliest date from an array of date strings
 * @param dates Array of date strings
 * @returns Formatted date string or '-' if no valid dates
 */
export const getEarliestDate = (dates: (string | null | undefined)[]): string => {
  const validDates = dates.filter(Boolean).map((d) => new Date(d as string));

  if (validDates.length === 0) {
    return '-';
  }

  const earliest = new Date(Math.min(...validDates.map((d) => d.getTime())));
  return earliest.toLocaleDateString();
};

/**
 * Get latest date from an array of date strings
 * @param dates Array of date strings
 * @returns Formatted date string or '-' if no valid dates
 */
export const getLatestDate = (dates: (string | null | undefined)[]): string => {
  const validDates = dates.filter(Boolean).map((d) => new Date(d as string));

  if (validDates.length === 0) {
    return '-';
  }

  const latest = new Date(Math.max(...validDates.map((d) => d.getTime())));
  return latest.toLocaleDateString();
};

/**
 * Calculate unique adset budgets from ads
 * @param ads Array of ads
 * @returns Map of adsetId to budget amount
 */
export const calculateUniqueAdsetBudgets = (
  ads: Array<{ adsetId: string; adsetDailyBudget?: number }>
): Map<string, number> => {
  const uniqueAdsetBudgets = new Map<string, number>();

  ads.forEach((ad) => {
    if (ad.adsetDailyBudget && !uniqueAdsetBudgets.has(ad.adsetId)) {
      uniqueAdsetBudgets.set(ad.adsetId, ad.adsetDailyBudget);
    }
  });

  return uniqueAdsetBudgets;
};

/**
 * Calculate total budget from unique adsets
 * @param ads Array of ads
 * @returns Total budget amount
 */
export const calculateTotalBudget = (
  ads: Array<{ adsetId: string; adsetDailyBudget?: number }>
): number => {
  const uniqueBudgets = calculateUniqueAdsetBudgets(ads);
  return Array.from(uniqueBudgets.values()).reduce((sum, budget) => sum + budget, 0);
};
